import {
  DumGlobalEventSubmissionListDto,
  DumQueryApi
} from '@src/Api/generated-global-event';
import { RootStore } from '@src/Store/Store';
import { makeAutoObservable } from 'mobx';
import { IDumFilter } from '../AdvanceFilter/AdvanceFilterStore';
import {
  IDumGlobalEventDetailHoverData,
  IMapCoordinate,
  IStatusApi,
  defaultStatusApi
} from '../DUMEvents/DUMEvents.type';
import { TEventItem, TEventType } from './MySubmission.interface';
import { reformatEventItem } from './reformatEventItem';
const PER_PAGE_DATA = 20;
interface FilterDataTypes extends Omit<IDumFilter, 'year'> {
  searchValue?: string;
  year?: number;
  countries: string[];
  status: string;
}
export interface IMySubmissionStore {
  show: boolean;
  filterData: FilterDataTypes;
  events: TEventItem[];
  status: IStatusApi;
  statusPreviousEvent: IStatusApi;
  canLoadMoreEvents: boolean;
  rawEvents: DumGlobalEventSubmissionListDto[];
  deleteSelected?: TEventItem;
  _api: DumQueryApi | null;
  _accountId: string;
  showDetail: boolean;
  detailData: IMapCoordinate | undefined;
  handleSelectField: (fieldName: string, value: unknown) => void;
  loadEvents: (accountId: string, _api: DumQueryApi) => Promise<void>;
  loadPreviousEvent: () => Promise<void>;
  onShowDetail: (detail: DumGlobalEventSubmissionListDto) => void;
}

export class MySubmissionStore implements IMySubmissionStore {
  show = false;
  filterData: FilterDataTypes = {
    countries: [] as string[],
    year: undefined,
    anomalyRating: undefined,
    status: 'all'
  };
  events: TEventItem[] = [];
  deleteSelected?: TEventItem = undefined;
  rawEvents: DumGlobalEventSubmissionListDto[] = [];
  status: IStatusApi = defaultStatusApi;
  statusPreviousEvent: IStatusApi = { ...defaultStatusApi, isLoading: false };
  rootStore: RootStore;
  _api: DumQueryApi | null = null;
  _accountId = '';
  canLoadMoreEvents = false;
  showDetail = false;
  detailData: IMapCoordinate | undefined = undefined;
  private previousDumId: string | undefined = undefined;
  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  private checkCanLoadMore = <T>(data: T[]) => {
    if (data.length < PER_PAGE_DATA) {
      this.canLoadMoreEvents = false;
    } else {
      this.canLoadMoreEvents = true;
    }
  };

  loadEventCatchError = (err: unknown, isPreviousDumId = false) => {
    if (err instanceof Error) {
      const checkErr = `${err}`.includes('undefined')
        ? 'Error, Something went wrong. Please refresh or try again later'
        : err;
      if (isPreviousDumId) {
        this.statusPreviousEvent = {
          isSuccess: false,
          isLoading: false,
          isError: true,
          message: `${checkErr}`
        };
      } else {
        this.status = {
          isSuccess: false,
          isLoading: false,
          isError: true,
          message: `${checkErr}`
        };
        this.events = [];
      }
    }
  };

  validationCheckForLoadEvents = (
    accountId: string,
    _api: DumQueryApi,
    isPreviousDumId = false
  ) => {
    let shouldStop = false;
    if (isPreviousDumId) {
      this.statusPreviousEvent.isLoading = true;
    } else {
      this.previousDumId = undefined;
      this.status.isLoading = true;
      this.events = [];
    }
    if (!this._api) {
      this._api = _api;
    }
    if (!accountId) {
      this.status.isLoading = false;
      this.events = [];
      shouldStop = true;
    } else {
      this._accountId = accountId;
    }

    return { shouldStop };
  };

  async loadEvents(
    accountId: string,
    _api: DumQueryApi,
    isPreviousDumId = false
  ) {
    const { shouldStop } = this.validationCheckForLoadEvents(
      accountId,
      _api,
      isPreviousDumId
    );
    if (shouldStop) {
      return;
    }
    try {
      const results = await _api.dumAccountIdGet(
        accountId?.toString(),
        this.filterData.countries?.filter((v) => v !== undefined),
        this.filterData.anomalyRating?.map((v) => v),
        this.filterData.status !== 'all'
          ? [Number(this.filterData.status)]
          : undefined,
        this.previousDumId
      );
      if (results && results.status === 200 && results.data) {
        const data: DumGlobalEventSubmissionListDto[] = results.data
          .data as unknown as DumGlobalEventSubmissionListDto[];
        this.previousDumId = data[data.length - 1].id ?? undefined;
        this.rawEvents = data;
        this.events = [...this.events, ...reformatEventItem(this.rawEvents)];
        this.checkCanLoadMore(data);
        if (isPreviousDumId) {
          this.statusPreviousEvent = {
            isSuccess: true,
            isLoading: false,
            isError: false,
            message: ''
          };
        } else {
          this.status = {
            isSuccess: true,
            isLoading: false,
            isError: false,
            message: ''
          };
        }
      }
    } catch (err) {
      this.loadEventCatchError(err, isPreviousDumId);
    }
  }

  async loadPreviousEvent() {
    if (!this._api) {
      return;
    }

    this.loadEvents(this._accountId, this._api, true);
  }

  handleSelectField(fieldName: string, value: unknown) {
    this.filterData = {
      ...this.filterData,
      [fieldName]: value
    };
    if (this._api && this._accountId) {
      this.loadEvents(this._accountId, this._api);
    }
  }

  onShowDetail(detail: DumGlobalEventSubmissionListDto) {
    this.showDetail = true;
    const hoverData: IDumGlobalEventDetailHoverData[] = [];
    hoverData.push({
      globalEventId: detail.id,
      country: detail.location?.locationName,
      latlon: [detail.location?.latitude, detail.location?.longitude],
      investigation: detail.name,
      date: detail.eventDate,
      description: '',
      author: detail.author?.name,
      authorImageUrl:
        detail.author?.image_url && detail.author?.image_url !== 'url'
          ? detail.author?.image_url
          : null,
      locationName: detail.location?.locationName ?? 'Unknown location',
      reference: '',
      scaleOfEvent: 0,
      type: detail.product_type as TEventType,
      mediaUrl: ''
    });
    this.detailData = {
      city: detail.location?.locationName ?? 'Unknown Location',
      coordinates: [detail.location?.latitude, detail.location?.longitude],
      isMythicEvent: false,
      id: detail.dumId,
      hoverData
    } as IMapCoordinate;
  }
}

export const MySubmissionStoreDefault: IMySubmissionStore = {
  show: false,
  events: [],
  filterData: {} as FilterDataTypes,
  status: defaultStatusApi,
  _accountId: '',
  _api: null,
  statusPreviousEvent: defaultStatusApi,
  canLoadMoreEvents: false,
  showDetail: false,
  detailData: undefined,
  rawEvents: [],
  handleSelectField: () => {
    //
  },
  loadEvents: () => {
    //
    return Promise.resolve();
  },
  async loadPreviousEvent() {
    //
  },
  onShowDetail() {
    //
  }
};
